import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import Table from "@/common/elements/Table";
import { PERSON } from "@/components/InmateDetails/Personal";
import { useClientParamBool } from "@/hooks/useClientParam";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { staticFields } from "@/util/common_variable";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import {
  convertApiFields,
  findAgefromDOB,
  getFullAddress,
  getOptionsFromRefList,
  getRefListNameByID,
  getSQLTypeToHTMLType,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import { BookingsSectionTitle, StyledFlagInformation } from "../styles";

// Read-only view of a folder's (jacket's) Bio - the React counterpart of
// ASP's Jacket Details page (JacketsInterface.asp -> CreateHTMLInterfaceBio
// in Browse mode). Takes the bio as a prop instead of reading the open
// booking from redux, which is what PersonalInformationDetails and its child
// lists do (they're the booking's editable form).
//
// The main field grid is driven by the same staticFields.personalInfo config
// that the booking's Personal form (InmateDetails/Personal.jsx) renders, with
// the same client-param/security filtering, so fields, labels and order stay
// in step with it.

const EMPTY = "-";

const display = (value) =>
  isNonEmptyVariable(value) && String(value).trim() !== ""
    ? String(value)
    : EMPTY;

// Label a dropdown value the way its form Select would show it.
const optionLabel = (options, value) => {
  if (!isNonEmptyVariable(value)) return EMPTY;
  const match = (options || []).find(
    (o) => String(o?.value)?.trim() === String(value)?.trim(),
  );
  return display(match?.label ?? value);
};

const yesNo = (value) =>
  value === true || value === "Y" || value === 1 ? "Yes" : "No";

// Mirrors DynamicForm's rendering of one staticFields entry, read-only.
const formatField = (field, bio) => {
  const { columnName, type, refListName, isID, isStaff } = field;
  const value = bio?.[columnName];

  if (columnName === "ageAtEntry") {
    // Personal.jsx recomputes this from DOB (current age) - ASP shows "Age".
    return isNonEmptyVariable(bio?.dob)
      ? display(findAgefromDOB(dayjs(bio.dob).format("MM/DD/YYYY")))
      : EMPTY;
  }

  switch (getSQLTypeToHTMLType(type)) {
    case "select":
      return optionLabel(
        getOptionsFromRefList(
          refListName || columnName,
          !!isStaff,
          !!(
            field.longStringRequired ||
            ["teeth", "hairStyle"].includes(columnName)
          ),
          !!isID,
        ),
        value,
      );
    case "checkbox":
      return yesNo(value);
    case "date":
    case "datetime": {
      const parsed = isNonEmptyVariable(value) ? dayjs(value) : null;
      if (!parsed?.isValid()) return display(value);
      return parsed.format(
        getSQLTypeToHTMLType(type) === "date" ? "MM/DD/YYYY" : "MM/DD/YYYY HH:mm",
      );
    }
    default:
      return display(value);
  }
};

const ALIAS_HEADERS = [
  { key: "lName", text: "LName" },
  { key: "fName", text: "FName" },
  { key: "mName", text: "MName" },
  { key: "suffix", text: "Suffix" },
  { key: "dob", text: "Date of Birth" },
  { key: "ssn", text: "SSN" },
  { key: "questionedIdentity", text: "Questioned Identity" },
];

const ADDRESS_HEADERS = [
  { key: "type", text: "Type" },
  { key: "fullAddress", text: "Full Address", width: 600 },
];

const DC_HEADERS = [
  { key: "characteristicType", text: "Characteristic Type" },
  { key: "nist", text: "NIST" },
  { key: "location", text: "Location" },
  { key: "description", text: "Description" },
];

const MISC_HEADERS = [
  { key: "miscNumber", text: "Description" },
  { key: "number", text: "Number" },
];

const InfoField = ({ label, value, span = 6 }) => (
  <StyledGridCol span={span} style={{ marginBottom: "12px" }}>
    <label className="info-label">{label}</label>
    <div className="info-value">{value}</div>
  </StyledGridCol>
);

const SectionTitle = ({ children }) => (
  <BookingsSectionTitle style={{ marginBottom: "10px", marginTop: "20px" }}>
    <span>{children}</span>
  </BookingsSectionTitle>
);

// Additional Personal Information (BioExtra) for the given bio, read-only.
// Same two calls the booking's AdditionalPersonalInformation/useBioExtraSync
// make: the client-configured "_U" column defs (GET getBioExtra) and the
// saved row (GET extra/BioExtra/bioID/{bioId}) - but keyed off the folder's
// bio instead of the open booking's.
const useFolderBioExtra = (bioId, enabled) => {
  const [bioExtra, setBioExtra] = useState({ fields: [], values: {} });

  useEffect(() => {
    if (!enabled) {
      setBioExtra({ fields: [], values: {} });
      return undefined;
    }
    let cancelled = false;

    Promise.allSettled([
      api.get(API_ENDPOINTS.getBioExtra),
      bioId
        ? fetchExtraTableRecord("BioExtra", "bioID", bioId)
        : Promise.resolve(null),
    ]).then(([defsResult, recordResult]) => {
      if (cancelled) return;
      const defs =
        defsResult.status === "fulfilled"
          ? (defsResult.value?.data || []).filter((d) =>
              d?.columnName?.endsWith("_U"),
            )
          : [];
      setBioExtra({
        fields: convertApiFields(defs, "BioExtra"),
        values:
          recordResult.status === "fulfilled"
            ? recordResult.value?.values || {}
            : {},
      });
    });

    return () => {
      cancelled = true;
    };
  }, [bioId, enabled]);

  return bioExtra;
};

const FolderBioView = ({ bio, photo, isStaff = false, startingPoint = "" }) => {
  // ASP defaults IncludeBioExtra to True for most modules, False for Staff
  // (same default PersonalInformationDetails applies).
  const bioExtraContext = isStaff ? "Staff" : "Bookings";
  const includeBioExtra = useClientParamBool(
    bioExtraContext,
    "IncludeBioExtra",
    !isStaff,
  );
  const bioExtra = useFolderBioExtra(bio?.bioId, includeBioExtra);
  const bioExtraFields = bioExtra.fields.filter((sf) =>
    hasCheckSecurity(
      "Functionality",
      "field",
      "VIEW",
      "",
      "",
      sf.tableName,
      sf.columnName,
      "Bookings",
    ),
  );

  const showQuestionedIdentity = useClientParamBool(
    "Bio",
    "DisplayQuestionIdentity",
    false,
  );
  const multipleAliasesEnabled = useClientParamBool(
    "Bio",
    "MultipleAliases",
    false,
  );

  const fields = (staticFields?.personalInfo?.[PERSON.BOOKINGS] || []).filter(
    (sf) => {
      if (sf.columnName === "questionedIdentity") return showQuestionedIdentity;
      if (sf.hideWhenMultipleAliases) return !multipleAliasesEnabled;
      return hasCheckSecurity(
        "Functionality",
        "field",
        "VIEW",
        "",
        "",
        sf.tableName,
        sf.columnName,
        startingPoint,
      );
    },
  );

  const aliases = (bio?.bioAliases || []).map((a, idx) => ({
    ...a,
    id: a?.bioAliasId ?? idx,
    questionedIdentity: yesNo(a?.questionedIdentity),
  }));

  const addresses = (bio?.bioAddresses || []).map((a, idx) => ({
    id: a?.bioAddressId ?? idx,
    type: a?.addresses?.[0]?.addressType || "",
    fullAddress: getFullAddress(a?.addresses?.[0]),
  }));

  const characteristics = (bio?.distinguishingCharacteristics || []).map(
    (dc, idx) => ({
      id: dc?.distinguishingCharacteristicsID ?? idx,
      characteristicType: optionLabel(
        getOptionsFromRefList("DistinguishingCharacteristics"),
        dc?.characteristicType,
      ),
      nist: dc?.nist,
      location: optionLabel(
        getOptionsFromRefList("CharacteristicLocation"),
        dc?.location,
      ),
      description: dc?.description,
    }),
  );

  const miscNumbers = (bio?.miscellaneousNumber || []).map((m, idx) => ({
    id: idx,
    miscNumber: getRefListNameByID("Misc_Number", m?.miscNumberID),
    number: m?.number,
  }));

  return (
    <StyledFlagInformation>
      <StyledGridRow gutter={16}>
        <StyledGridCol span={photo ? 18 : 24}>
          <StyledGridRow gutter={16}>
            {fields.map((sf) => (
              <InfoField
                key={sf.columnName}
                label={sf.label}
                value={formatField(sf, bio)}
              />
            ))}
          </StyledGridRow>
        </StyledGridCol>
        {photo && <StyledGridCol span={6}>{photo}</StyledGridCol>}
      </StyledGridRow>

      {!isStaff && multipleAliasesEnabled && (
        <>
          <SectionTitle>List of aliases</SectionTitle>
          <Table headers={ALIAS_HEADERS} data={aliases} />
        </>
      )}

      <SectionTitle>List of addresses</SectionTitle>
      <Table headers={ADDRESS_HEADERS} data={addresses} />

      {!isStaff && (
        <>
          <SectionTitle>Place of Birth</SectionTitle>
          <StyledGridRow gutter={16}>
            <InfoField label="FBI" value={display(bio?.fbi)} />
            <InfoField label="SID" value={display(bio?.sid)} />
            <InfoField
              label="POB Type"
              value={optionLabel(
                getOptionsFromRefList("POBType", false, false, true),
                bio?.pobType,
              )}
            />
            <InfoField
              label="POB NCIC"
              value={optionLabel(
                getOptionsFromRefList("POBNCIC", false, false, true),
                bio?.pobNciCid,
              )}
            />
            <InfoField
              label="Country of Citizenship"
              value={optionLabel(
                getOptionsFromRefList("POBNCIC", false, false, true),
                bio?.countryOfCitizenshipId,
              )}
            />
            <InfoField
              label="Primary Language"
              value={optionLabel(
                getOptionsFromRefList("LanguageList"),
                bio?.primaryLanguage,
              )}
            />
          </StyledGridRow>

          <SectionTitle>Distinguishing Characteristics</SectionTitle>
          <Table headers={DC_HEADERS} data={characteristics} />

          <SectionTitle>Misc Numbers</SectionTitle>
          <Table headers={MISC_HEADERS} data={miscNumbers} />
        </>
      )}

      {/* Hidden when the client has no BioExtra columns configured, like
          the booking form. */}
      {includeBioExtra && bioExtraFields.length > 0 && (
        <>
          <SectionTitle>Additional Personal Information</SectionTitle>
          <StyledGridRow gutter={16}>
            {bioExtraFields.map((sf) => (
              <InfoField
                key={sf.columnName}
                label={sf.label}
                value={formatField(sf, bioExtra.values)}
              />
            ))}
          </StyledGridRow>
        </>
      )}
    </StyledFlagInformation>
  );
};

export default FolderBioView;
